import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, inject, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { StateService } from '@uirouter/core';
import { TranslocoModule } from '@jsverse/transloco';
import { NotificationType } from '@client/app/feature-modules/navigation/notifications/notifications.types';
import { NotificationsRepoService } from '@client/app/feature-modules/navigation/notifications/services/notifications-repo.service';
import { NotificationsRedirectService } from '@client/app/feature-modules/navigation/notifications/services/notifications-redirect.service';
import { NotificationActionPipe } from '@client/app/feature-modules/navigation/notifications/pipes/notification-action.pipe';
import { NotificationNamePipe } from '@client/app/feature-modules/navigation/notifications/pipes/notification-name.pipe';
import { MomentsModule } from '@client/app/modules/moments/moments.module';
import { SessionService } from '@monsido/core/session/session.service';
import { ButtonModule } from 'primeng/button';
import { FocusTrap } from 'primeng/focustrap';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
    selector: 'mon-notifications-panel',
    templateUrl: './notifications-panel.component.html',
    styleUrls: ['./notifications-panel.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        NotificationActionPipe,
        NotificationNamePipe,
        MomentsModule,
        ProgressSpinnerModule,
        ButtonModule,
        FocusTrap,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsPanelComponent implements OnInit {
    @Output() readonly closeNotificationPanel = new EventEmitter<void>();

    now: Date = new Date();

    private readonly notificationsRepoService = inject(NotificationsRepoService);
    private readonly notificationsRedirectService = inject(NotificationsRedirectService);
    private readonly stateService = inject(StateService);
    private readonly sessionService = inject(SessionService);
    private readonly elementRef = inject(ElementRef);

    private clickListenerReady = false;

    protected readonly notifications: WritableSignal<NotificationType[]> = signal<NotificationType[]>([]);
    protected readonly loading: WritableSignal<boolean> = signal<boolean>(false);

    ngOnInit (): void {
        void this.fetch();
        this.now = new Date();

        // Defer activation of the click-outside listener by one tick so the
        // same click that opened the panel does not immediately close it.
        setTimeout(() => {
            this.clickListenerReady = true;
        }, 0);
    }

    @HostListener('document:keydown.escape')
    onEscKey (): void {
        this.closeNotificationPanel.emit();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick (event: MouseEvent): void {
        if (!this.clickListenerReady) {
            return;
        }
        const panelElement = this.elementRef.nativeElement as HTMLElement;
        if (event.target && !panelElement.contains(event.target as Node)) {
            this.closeNotificationPanel.emit();
        }
    }

    private async fetch (): Promise<void> {
        this.loading.set(true);
        try {
            const notifications = await this.notificationsRepoService.getAll({ page_size: 5, filter: 'new' });
            this.notifications.set(notifications.slice(0, 5));
        } catch {
            // silently handle errors
        } finally {
            this.loading.set(false);
        }
    }

    redirect (event: Event, notification: NotificationType): void {
        event.preventDefault();
        event.stopPropagation();
        this.notificationsRedirectService.redirect({
            scopes: Array.isArray(notification.scopes) ? notification.scopes : [notification.scopes],
            action: notification.action,
            target: notification.target,
            target_id: notification.target_id,
        });
        this.closeNotificationPanel.emit();
    }

    viewAll (): void {
        const customerId = this.sessionService.getCustomerId();
        this.stateService.go('base.customer.notifications', { customerId });
        this.closeNotificationPanel.emit();
    }

    onClose (): void {
        this.closeNotificationPanel.emit();
    }

    trackById (notification: NotificationType): number {
        return notification.id;
    }
}
